import { useEffect, useRef, useState, useCallback } from 'react'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'

const CARD_SIZE = 1080 // px — square, Instagram/Facebook friendly

// ── Style definitions ──────────────────────────────────────────────────────
const STYLES = [
  {
    key: 'white',
    label: 'Clean white',
    desc: 'Minimal, professional',
    preview: { bg: WHITE, text: TEXT, sub: MUTED, accent: BLUE, border: '#E4E6EB' },
  },
  {
    key: 'dark',
    label: 'Dark premium',
    desc: 'Bold, luxury feel',
    preview: { bg: '#0B1120', text: WHITE, sub: '#A0AEC0', accent: '#63B3ED', border: '#1E2D45' },
  },
  {
    key: 'blue',
    label: 'Brand blue',
    desc: 'Facebook blue, energetic',
    preview: { bg: BLUE, text: WHITE, sub: 'rgba(255,255,255,0.75)', accent: WHITE, border: 'rgba(255,255,255,0.2)' },
  },
]

// ── Canvas card generator ──────────────────────────────────────────────────
async function generateCardCanvas({ imageUrl, productName, productCode, shopName, styleKey, watermark = false }) {
  const style = STYLES.find(s => s.key === styleKey) || STYLES[0]
  const c = style.preview
  const SIZE = CARD_SIZE

  const canvas = document.createElement('canvas')
  canvas.width  = SIZE
  canvas.height = SIZE
  const ctx = canvas.getContext('2d')

  // ── Background ──
  ctx.fillStyle = c.bg
  ctx.fillRect(0, 0, SIZE, SIZE)

  // ── Subtle grid/texture for dark ──
  if (styleKey === 'dark') {
    ctx.strokeStyle = 'rgba(255,255,255,0.03)'
    ctx.lineWidth = 1
    for (let x = 0; x < SIZE; x += 60) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, SIZE); ctx.stroke()
    }
    for (let y = 0; y < SIZE; y += 60) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(SIZE, y); ctx.stroke()
    }
  }

  // ── Blue gradient overlay ──
  if (styleKey === 'blue') {
    const grad = ctx.createLinearGradient(0, 0, SIZE, SIZE)
    grad.addColorStop(0, '#1877F2')
    grad.addColorStop(1, '#0d5abf')
    ctx.fillStyle = grad
    ctx.fillRect(0, 0, SIZE, SIZE)
  }

  // ── Product image ──
  const imgAreaTop    = 80
  const imgAreaHeight = 640
  const imgPad        = 60

  if (imageUrl) {
    try {
      const img = await loadImage(imageUrl)
      // Draw image with rounded rect clip
      const ix = imgPad
      const iy = imgAreaTop
      const iw = SIZE - imgPad * 2
      const ih = imgAreaHeight

      ctx.save()
      roundRect(ctx, ix, iy, iw, ih, 32)
      ctx.clip()

      // Cover fit
      const scale = Math.max(iw / img.width, ih / img.height)
      const sw = img.width * scale
      const sh = img.height * scale
      const sx = ix + (iw - sw) / 2
      const sy = iy + (ih - sh) / 2
      ctx.drawImage(img, sx, sy, sw, sh)
      ctx.restore()

      // Subtle image border
      ctx.strokeStyle = styleKey === 'white' ? '#E4E6EB' : 'rgba(255,255,255,0.15)'
      ctx.lineWidth = 2
      roundRect(ctx, ix, iy, iw, ih, 32)
      ctx.stroke()

    } catch {
      // Fallback — draw placeholder
      drawPlaceholder(ctx, imgPad, imgAreaTop, SIZE - imgPad * 2, imgAreaHeight, styleKey)
    }
  } else {
    drawPlaceholder(ctx, imgPad, imgAreaTop, SIZE - imgPad * 2, imgAreaHeight, styleKey)
  }

  // ── Bottom info area ──
  const bottomY = imgAreaTop + imgAreaHeight + 32

  // Shop name
  ctx.fillStyle = c.accent
  ctx.font = `700 38px 'Arial', sans-serif`
  ctx.textAlign = 'center'
  const shopDisplay = shopName || 'Your Shop'
  ctx.fillText(truncate(ctx, shopDisplay, SIZE - 120), SIZE / 2, bottomY)

  // Product name
  ctx.fillStyle = c.text
  ctx.font = `600 52px 'Arial', sans-serif`
  const nameY = bottomY + 62
  const nameLines = wrapText(ctx, productName || 'Product Name', SIZE - 120)
  nameLines.slice(0, 2).forEach((line, i) => {
    ctx.fillText(line, SIZE / 2, nameY + i * 60)
  })

  // Product code pill
  const codeY = nameY + Math.min(nameLines.length, 2) * 60 + 30
  const code = productCode || ''
  if (code) {
    ctx.font = `700 30px 'Courier New', monospace`
    const codeW = ctx.measureText(code).width + 48
    const pillX = (SIZE - codeW) / 2
    const pillH = 52
    const pillY = codeY - 34

    // Pill bg
    ctx.fillStyle = styleKey === 'white' ? '#E7F3FF' : 'rgba(255,255,255,0.15)'
    roundRect(ctx, pillX, pillY, codeW, pillH, 26)
    ctx.fill()

    ctx.fillStyle = styleKey === 'white' ? BLUE : c.accent
    ctx.textAlign = 'center'
    ctx.fillText(code, SIZE / 2, pillY + 34)
  }

  // ── Watermark ──
  if (watermark && shopName) {
    ctx.save()
    ctx.globalAlpha = 0.12
    ctx.fillStyle = styleKey === 'white' ? '#000000' : '#FFFFFF'
    ctx.font = `bold ${Math.round(SIZE * 0.035)}px Arial`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.translate(SIZE / 2, SIZE / 2)
    ctx.rotate(-Math.PI / 6)
    const wmText = '© ' + shopName
    const step = SIZE * 0.28
    for (let dy = -SIZE; dy < SIZE; dy += step) {
      ctx.fillText(wmText, 0, dy)
    }
    ctx.restore()
  }

  // ── Powered by — subtle bottom ──
  ctx.fillStyle = styleKey === 'white' ? '#B0B3B8' : 'rgba(255,255,255,0.35)'
  ctx.font = `400 24px 'Arial', sans-serif`
  ctx.textAlign = 'center'
  ctx.fillText('Powered by pageconnect.io', SIZE / 2, SIZE - 32)

  return canvas
}

function drawPlaceholder(ctx, x, y, w, h, styleKey) {
  ctx.fillStyle = styleKey === 'white' ? '#F0F2F5' : 'rgba(255,255,255,0.08)'
  roundRect(ctx, x, y, w, h, 32)
  ctx.fill()
  ctx.fillStyle = styleKey === 'white' ? '#C0C4CC' : 'rgba(255,255,255,0.3)'
  ctx.font = '40px Arial'
  ctx.textAlign = 'center'
  ctx.fillText('📦', x + w / 2, y + h / 2 + 16)
  ctx.font = '28px Arial'
  ctx.fillStyle = styleKey === 'white' ? '#B0B3B8' : 'rgba(255,255,255,0.4)'
  ctx.fillText('No product image', x + w / 2, y + h / 2 + 68)
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

function truncate(ctx, text, maxW) {
  if (ctx.measureText(text).width <= maxW) return text
  while (text.length > 0 && ctx.measureText(text + '...').width > maxW) text = text.slice(0, -1)
  return text + '...'
}

function wrapText(ctx, text, maxW) {
  const words = text.split(' ')
  const lines = []
  let current = ''
  for (const word of words) {
    const test = current ? current + ' ' + word : word
    if (ctx.measureText(test).width > maxW && current) {
      lines.push(current)
      current = word
    } else {
      current = test
    }
  }
  if (current) lines.push(current)
  return lines
}

// ── Mini preview card (shown in modal) ────────────────────────────────────
function StylePreview({ styleKey, productName, productCode, shopName, imageUrl, active, onClick }) {
  const canvasRef = useRef(null)
  const style = STYLES.find(s => s.key === styleKey)
  const c = style.preview

  useEffect(() => {
    if (!canvasRef.current) return
    const SIZE = 220
    const canvas = canvasRef.current
    canvas.width  = SIZE
    canvas.height = SIZE
    const ctx = canvas.getContext('2d')

    // Background
    if (styleKey === 'blue') {
      const grad = ctx.createLinearGradient(0, 0, SIZE, SIZE)
      grad.addColorStop(0, '#1877F2')
      grad.addColorStop(1, '#0d5abf')
      ctx.fillStyle = grad
    } else {
      ctx.fillStyle = c.bg
    }
    ctx.fillRect(0, 0, SIZE, SIZE)

    // Draw async image
    const drawContent = async () => {
      if (imageUrl) {
        try {
          const img = await loadImage(imageUrl)
          const imgH = 130
          ctx.save()
          roundRect(ctx, 12, 12, SIZE - 24, imgH, 10)
          ctx.clip()
          const scale = Math.max((SIZE - 24) / img.width, imgH / img.height)
          const sw = img.width * scale
          const sh = img.height * scale
          ctx.drawImage(img, 12 + ((SIZE - 24) - sw) / 2, 12 + (imgH - sh) / 2, sw, sh)
          ctx.restore()
        } catch {
          ctx.fillStyle = styleKey === 'white' ? '#F0F2F5' : 'rgba(255,255,255,0.1)'
          roundRect(ctx, 12, 12, SIZE - 24, 130, 10)
          ctx.fill()
        }
      } else {
        ctx.fillStyle = styleKey === 'white' ? '#F0F2F5' : 'rgba(255,255,255,0.1)'
        roundRect(ctx, 12, 12, SIZE - 24, 130, 10)
        ctx.fill()
        ctx.fillStyle = styleKey === 'white' ? '#C0C4CC' : 'rgba(255,255,255,0.3)'
        ctx.font = '24px Arial'
        ctx.textAlign = 'center'
        ctx.fillText('📦', SIZE / 2, 84)
      }

      ctx.textAlign = 'center'
      ctx.fillStyle = c.accent
      ctx.font = `700 11px Arial`
      ctx.fillText(truncate(ctx, shopName || 'Shop', SIZE - 24), SIZE / 2, 160)

      ctx.fillStyle = c.text
      ctx.font = `600 13px Arial`
      ctx.fillText(truncate(ctx, productName || 'Product', SIZE - 24), SIZE / 2, 178)

      if (productCode) {
        ctx.fillStyle = styleKey === 'white' ? '#93C5FD' : 'rgba(255,255,255,0.6)'
        ctx.font = `700 10px Courier New`
        ctx.fillText(productCode, SIZE / 2, 196)
      }

      ctx.fillStyle = styleKey === 'white' ? '#D1D5DB' : 'rgba(255,255,255,0.25)'
      ctx.font = `9px Arial`
      ctx.fillText('pageconnect.io', SIZE / 2, SIZE - 8)
    }

    drawContent()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleKey, productName, productCode, shopName, imageUrl])

  return (
    <button onClick={onClick}
      style={{ border: '2px solid', borderColor: active ? BLUE : BORDER, borderRadius: '12px', overflow: 'hidden', cursor: 'pointer', padding: 0, background: 'none', display: 'block', width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: 'auto', display: 'block' }} />
      <div style={{ padding: '8px', background: active ? '#E7F3FF' : WHITE, textAlign: 'center' }}>
        <div style={{ fontSize: '12.5px', fontWeight: 700, color: active ? BLUE : TEXT }}>{style.label}</div>
        <div style={{ fontSize: '11px', color: MUTED }}>{style.desc}</div>
      </div>
    </button>
  )
}

// ── Main modal ─────────────────────────────────────────────────────────────
export function ProductCardModal({ product, shopName, onClose }) {
  const [selectedStyle, setSelectedStyle] = useState('white')
  const [generating, setGenerating]       = useState(false)
  const [generated, setGenerated]         = useState(false)
  const [watermark, setWatermark]         = useState(false)

  const imageUrl   = product.image_url ? product.image_url + '?t=' + Date.now() : null
  const productName = product.name || 'Product'
  const productCode = product.product_code || ''

  const handleDownload = useCallback(async () => {
    setGenerating(true)
    setGenerated(false)
    try {
      const canvas = await generateCardCanvas({
        imageUrl, productName, productCode, shopName, styleKey: selectedStyle, watermark,
      })
      const link = document.createElement('a')
      const filename = (productCode || productName).replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 30)
      link.download = filename + '_card_' + selectedStyle + '.png'
      link.href = canvas.toDataURL('image/png', 1.0)
      link.click()
      setGenerated(true)
      setTimeout(() => setGenerated(false), 3000)
    } catch (e) {
      console.error('Card generation failed:', e)
    }
    setGenerating(false)
  }, [imageUrl, productName, productCode, shopName, selectedStyle, watermark])

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 60 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: WHITE, borderRadius: '20px 20px 0 0', width: '100%', maxWidth: '580px', maxHeight: '92vh', overflow: 'auto', fontFamily: 'Space Grotesk, sans-serif' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + BORDER, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: WHITE, zIndex: 1 }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: TEXT }}>Generate product card</div>
            <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>{productName} — 1080×1080px, ready to post</div>
          </div>
          <button onClick={onClose}
            style={{ width: '28px', height: '28px', borderRadius: '50%', background: BG, border: 'none', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED }}>
            ×
          </button>
        </div>

        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '18px' }}>

          {/* No image warning */}
          {!imageUrl && (
            <div style={{ padding: '10px 14px', borderRadius: '10px', background: '#FFF8E6', border: '1px solid #FDE68A', fontSize: '12px', color: '#92400E', lineHeight: 1.6 }}>
              This product has no image — the card will show a placeholder. Add a product photo in the product edit section for a better card.
            </div>
          )}

          {/* Style selector */}
          <div>
            <div style={{ fontSize: '11.5px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>
              Choose style — click to select
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
              {STYLES.map(s => (
                <StylePreview
                  key={s.key}
                  styleKey={s.key}
                  productName={productName}
                  productCode={productCode}
                  shopName={shopName}
                  imageUrl={imageUrl}
                  active={selectedStyle === s.key}
                  onClick={() => setSelectedStyle(s.key)}
                />
              ))}
            </div>
          </div>

          {/* Card info */}
          <div style={{ padding: '12px 14px', borderRadius: '10px', background: BG, fontSize: '12px', color: MUTED, lineHeight: 1.7 }}>
            <strong style={{ color: TEXT }}>What's on the card:</strong> Product photo · Shop name · Product name · Product code
            <br />
            <strong style={{ color: TEXT }}>Size:</strong> 1080×1080px PNG — perfect for Facebook, Instagram and WhatsApp posts
            <br />
            <strong style={{ color: TEXT }}>Price:</strong> Not shown — add in your caption
          </div>

          {/* Watermark toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: '10px', background: BG, border: '1px solid ' + BORDER }}>
            <div>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT }}>Watermark</div>
              <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>Add a soft repeating shop name to protect against image theft</div>
            </div>
            <button onClick={() => setWatermark(w => !w)}
              style={{ width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', background: watermark ? BLUE : '#D1D5DB', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
              <div style={{ position: 'absolute', top: '2px', left: watermark ? '22px' : '2px', width: '20px', height: '20px', borderRadius: '50%', background: WHITE, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </button>
          </div>

          {/* Download button */}
          <button onClick={handleDownload} disabled={generating}
            style={{ width: '100%', padding: '14px', borderRadius: '12px', background: generating ? BORDER : generated ? '#16A34A' : BLUE, border: 'none', fontSize: '14px', fontWeight: 700, color: WHITE, cursor: generating ? 'not-allowed' : 'pointer', transition: 'background 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {generating ? (
              <>
                <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: WHITE, animation: 'pc-spin 0.7s linear infinite' }} />
                Generating...
              </>
            ) : generated ? (
              '✓ Downloaded!'
            ) : (
              '⬇ Download card — ' + (STYLES.find(s => s.key === selectedStyle)?.label || '')
            )}
          </button>

          <div style={{ fontSize: '11.5px', color: MUTED, textAlign: 'center', paddingBottom: '4px' }}>
            The card downloads as a PNG file. Post directly to Facebook, Instagram or WhatsApp.
          </div>
        </div>
      </div>
      <style>{`@keyframes pc-spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}

export default ProductCardModal